import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {CommonModule, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonModule} from '@angular/material/button';
import {GoogleMap, GoogleMapsModule, MapAdvancedMarker} from '@angular/google-maps';
import {DestinationType} from '../../destinations.types';
import {Subject, takeUntil} from 'rxjs';
import {RestFactoryService} from '../../../../../core/services/rest-factory.service';
import {FuseConfirmationService} from '../../../../../../@fuse/services/confirmation';
import {MatCardModule} from '@angular/material/card';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatDividerModule} from '@angular/material/divider';

@Component({
    selector: 'app-map-tab',
    templateUrl: './map-tab.component.html',
    styleUrls: ['./map-tab.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        NgIf,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        GoogleMapsModule,
        MapAdvancedMarker,
        MatCardModule,
        MatExpansionModule,
        MatDividerModule,
    ],
})
export class MapTabComponent implements OnChanges, OnDestroy, AfterViewInit {
    @Input() destination: DestinationType;
    @Input() reviewMode: boolean = false;
    @Output() save = new EventEmitter<void>();

    @ViewChild('googleMap', {static: false}) googleMapRef!: GoogleMap;

    googleAddressSearch: string;
    typingTimeout: any;

    loadMapVar: boolean = false;
    mapCenter: any;
    markersList: any[] = [];
    centralMarker: any;
    firstMarker: any;
    secondMarker: any;
    thirdMarker: any;
    fourthMarker: any;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private API_VERIFY_GOOGLE_ADDRESS = "/admin/destination/checklocation";

    constructor(
        private _restFactoryService: RestFactoryService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.destination && changes.destination.currentValue && !this.loadMapVar) {
            this.mapAngularInit().then();
        }
    }

    ngAfterViewInit(): void {
        this.mapAngularInit().then();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    isMissingData(val: any): boolean {
        if (val === null || val === undefined) {
            return true;
        }
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed.length === 0 || trimmed.endsWith('content/undefined')) {
                return true;
            }
        }
        return false;
    }

    isMissingCoordinate(val: any): boolean {
        if (val === null || val === undefined || val === '') {
            return true;
        }
        const num = Number(val);
        return isNaN(num) || num === 0;
    }

    async mapAngularInit() {
        if (!this.destination || this.loadMapVar) {
            return;
        }

        if (this.destination.vr_long && this.destination.vr_lat) {
            this.mapCenter = {lat: this.destination.vr_lat, lng: this.destination.vr_long};
        }

        const {PinElement} = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        this.markersList = [];

        this.centralMarker = {
            position: {lat: this.destination.vr_lat, lng: this.destination.vr_long},
            content: new PinElement({background: '#FF0000'}).element,
            gmpDraggable: !this.reviewMode,
            title: "Central"
        };
        this.markersList.push(this.centralMarker);

        if (this.destination.bl_locationboundaries) {
            this.firstMarker = {
                position: {lat: this.destination.vr_northwestlat, lng: this.destination.vr_northwestlong},
                content: new PinElement({background: '#0000FF'}).element,
                gmpDraggable: !this.reviewMode,
                title: "First"
            };
            this.secondMarker = {
                position: {lat: this.destination.vr_northeasttlat, lng: this.destination.vr_northeastlong},
                content: new PinElement({background: '#00FF00'}).element,
                gmpDraggable: !this.reviewMode,
                title: "Second"
            };
            this.thirdMarker = {
                position: {lat: this.destination.vr_southwestlat, lng: this.destination.vr_southwestlong},
                content: new PinElement({background: '#800000'}).element,
                gmpDraggable: !this.reviewMode,
                title: "Third"
            };
            this.fourthMarker = {
                position: {lat: this.destination.vr_southeastlat, lng: this.destination.vr_southeastlong},
                content: new PinElement({background: '#808080'}).element,
                gmpDraggable: !this.reviewMode,
                title: "Fourth"
            };
            this.markersList.push(this.firstMarker, this.secondMarker, this.thirdMarker, this.fourthMarker);
        }

        this.loadMapVar = true;
        this._changeDetectorRef.detectChanges();
    }

    clickGoogleMap(event: any) {
        if (this.reviewMode) return;
        this.centralMarker.position = {lat: event.latLng.lat(), lng: event.latLng.lng()};
        this.destination.vr_lat = event.latLng.lat();
        this.destination.vr_long = event.latLng.lng();
        this._changeDetectorRef.detectChanges();
        this.save.emit();
    }

    onMarkerDragEnd(event: any, marker: any) {
        if (this.reviewMode) return;
        const newPosition = {lat: event.latLng.lat(), lng: event.latLng.lng()};
        marker.position = newPosition;

        switch (marker.title) {
            case "Central":
                this.destination.vr_lat = newPosition.lat;
                this.destination.vr_long = newPosition.lng;
                break;
            case "First":
                this.destination.vr_northwestlat = newPosition.lat;
                this.destination.vr_northwestlong = newPosition.lng;
                break;
            case "Second":
                this.destination.vr_northeasttlat = newPosition.lat;
                this.destination.vr_northeastlong = newPosition.lng;
                break;
            case "Third":
                this.destination.vr_southwestlat = newPosition.lat;
                this.destination.vr_southwestlong = newPosition.lng;
                break;
            case "Fourth":
                this.destination.vr_southeastlat = newPosition.lat;
                this.destination.vr_southeastlong = newPosition.lng;
                break;
        }
        this._changeDetectorRef.detectChanges();
        this.save.emit();
    }

    verifyGoogleAddress() {
        const payload = new FormData();
        payload.append("ds_address", this.googleAddressSearch);
        payload.append("id_destination", this.destination.id_destination);

        this._restFactoryService.httpPostService(this.API_VERIFY_GOOGLE_ADDRESS, payload, "destination")
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    if (response.google_sugested) {
                        this.destination.ds_googlematch = response.google_sugested;
                        this.destination.ds_googleaddress = response.formatted_address;
                        this.destination.vr_lat = response.location_lat;
                        this.destination.vr_long = response.location_long;
                        this.centralMarker.position = {lat: response.location_lat, lng: response.location_long};
                        this.destination.vr_northwestlat = response.northeast_lat;
                        this.destination.vr_northwestlong = response.southwest_long;
                        this.firstMarker.position = {lat: response.northeast_lat, lng: response.southwest_long};
                        this.destination.vr_northeasttlat = response.northeast_lat;
                        this.destination.vr_northeastlong = response.northeast_long;
                        this.secondMarker.position = {lat: response.northeast_lat, lng: response.northeast_long};
                        this.destination.vr_southwestlat = response.southwest_lat;
                        this.destination.vr_southwestlong = response.southwest_long;
                        this.thirdMarker.position = {lat: response.southwest_lat, lng: response.southwest_long};
                        this.destination.vr_southeastlat = response.southwest_lat;
                        this.destination.vr_southeastlong = response.northeast_long;
                        this.fourthMarker.position = {lat: response.southwest_lat, lng: response.northeast_long};
                        this.goToMarker(this.centralMarker);
                        this._changeDetectorRef.detectChanges();
                    } else {
                        this._fuseConfirmationService.open({
                            title: "Error!",
                            message: "No address found. Please try again.",
                            actions: {confirm: {show: false}, cancel: {show: false}},
                            dismissible: true
                        });
                    }
                }
            });
    }

    goToMarker(marker: any) {
        if (!this.googleMapRef?.googleMap || !marker?.position) return;
        this.googleMapRef.googleMap.setCenter(marker.position);
    }

    onCoordinateKeyup(event: any, value: number, obj: any, objKey: string, isLat: boolean): void {
        if (event) event.preventDefault();
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.destination[objKey] = Number(value);
            if (isLat) {
                obj.position.lat = Number(value);
            } else {
                obj.position.lng = Number(value);
            }
            this._changeDetectorRef.detectChanges();
            this.save.emit();
        }, 500);
    }
}
